import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material';
import { LicenseManager } from 'ag-grid-enterprise';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import Header from '@/components/Header/Header';
import ApplicationLoadingPage from '@/components/UI/ApplicationLoadingPage/ApplicationLoadingPage';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import Spinner from '@/components/UI/Spinner/Spinner';
import Administration from '@/containers/Administration/Administration';
import CampaignDetail from '@/containers/CampaignDetail/CampaignDetail';
import CampaignRequestsOpenSearch from '@/containers/CampaignRequests/CampaignRequestsOpenSearch';
import Dashboard from '@/containers/Dashboard/Dashboard';
import DeniedPage from '@/containers/DeniedPage/DeniedPage';
import Files from '@/containers/Files/Files';
import FilesList from '@/containers/FilesList/FilesList';
import Login from '@/containers/Login/Login';
import StandIn from '@/containers/StandIn/StandIn';
import SubCampaignDetail from '@/containers/SubCampaignDetail/SubCampaignDetail';
import SubCROverviewOpenSearch from '@/containers/SubCROverview/SubCROverviewOpenSearch';
import { ErrorContextProvider } from '@/contexts/ErrorContext';
import { GlobalSpinnerContextProvider } from '@/contexts/GlobalSpinnerContext';
import { ToastContextProvider } from '@/contexts/ToastContext';
import { ROUTES, getAbsoluteRoute, STORAGE, BASE_PATH, isFileOnlyUser } from '@/helpers/constants';
import { SESSION_EXPIRED } from '@/helpers/events';
import useAuth from '@/hooks/useAuth';
import store from '@/redux/store';
import singletonEventService from '@/services/eventService';
import { appTheme } from '@/utils/componentStyles';
import { SocketSessionProvided } from './contexts/SocketContext';
import './App.scss';

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID || '');

const RedirectHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const authService = useAuth();
  const hasRedirectedRef = React.useRef(false);

  useEffect(() => {
    const isRootPath = location.pathname === '/' || location.pathname === BASE_PATH || location.pathname === `${BASE_PATH}/`;
    const isLoginPath = location.pathname === getAbsoluteRoute(ROUTES.LOGIN);

    if (!isRootPath && !isLoginPath) {
      sessionStorage.setItem(STORAGE.LAST_ROUTE, `${location.pathname}${location.search}`);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (authService.canSeeFile() && isFileOnlyUser(authService)) {
      setTimeout(() => {
        navigate(getAbsoluteRoute(ROUTES.FILES_LIST), { replace: true });
      }, 0);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasRedirectedRef.current) {
      return;
    }

    const isRootPath = location.pathname === '/' || location.pathname === BASE_PATH || location.pathname === `${BASE_PATH}/`;
    const userHasRoles = authService.canAccess() || authService.canSeeFile();

    if (!isRootPath && !authService.isLogged) {
      return;
    }

    if (!authService.isLoading && authService.isLogged && userHasRoles && isRootPath) {
      const savedURL = sessionStorage.getItem(STORAGE.SIGN_IN_REDIRECT);
      const lastRoute = sessionStorage.getItem(STORAGE.LAST_ROUTE);
      const filesListPath = getAbsoluteRoute(ROUTES.FILES_LIST);
      const dashboardPath = getAbsoluteRoute(ROUTES.DASHBOARD);
      const targetPath = isFileOnlyUser(authService) ? filesListPath : dashboardPath;

      if (savedURL && savedURL !== getAbsoluteRoute(ROUTES.LOGIN) && savedURL !== '/' && savedURL !== getAbsoluteRoute('/') && savedURL !== BASE_PATH && savedURL !== '/front/') {
        let urlToNavigate = savedURL;

        if (!savedURL.startsWith(BASE_PATH)) {
          urlToNavigate = BASE_PATH + savedURL;
        }

        sessionStorage.removeItem(STORAGE.SIGN_IN_REDIRECT);
        hasRedirectedRef.current = true;

        setTimeout(() => {
          navigate(urlToNavigate, { replace: true });
        }, 0);

        return;
      }

      if (lastRoute && lastRoute !== getAbsoluteRoute(ROUTES.LOGIN) && lastRoute !== '/' && lastRoute !== getAbsoluteRoute('/') && lastRoute !== BASE_PATH && lastRoute !== '/front/') {
        let urlToNavigate = lastRoute;

        if (!lastRoute.startsWith(BASE_PATH)) {
          urlToNavigate = BASE_PATH + lastRoute;
        }

        hasRedirectedRef.current = true;

        setTimeout(() => {
          navigate(urlToNavigate, { replace: true });
        }, 0);

        return;
      }

      if (location.pathname !== targetPath) {
        sessionStorage.removeItem(STORAGE.SIGN_IN_REDIRECT);
        hasRedirectedRef.current = true;
        navigate(targetPath, { replace: true });
      }
    }
  }, [authService.isLoading, authService.isLogged, location.pathname, navigate, authService]);

  return null;
};

const App = () => {
  const theme = createTheme(appTheme);
  const authService = useAuth();
  const { t } = useTranslation();
  const hasActiveToken = Boolean(authService.auth?.user && !authService.auth.user.expired);
  const isRehydratingSession = !authService.isLoading && !authService.isLogged && hasActiveToken;
  const shouldRenderLoginRoutes = !authService.isLoading && !authService.isLogged && !hasActiveToken;

  const [toggleMenu, setToggleMenu] = useState<(fromAdminTile: boolean) => void>(() => { });
  const [dialog, setDialog] = useState({
    show: false,
    title: '',
    message: '',
    btnText: '',
    action: () => { },
  });

  useEffect(() => {
    const subscription = singletonEventService
      .subscribe(SESSION_EXPIRED, () => setDialog({
        show: true,
        title: '',
        message: 'authentication.sessionEnd',
        btnText: 'dialog.close',
        action: () => { window.location.href = getAbsoluteRoute(ROUTES.LOGIN); },
      }));

    return () => singletonEventService.unsubscribe(SESSION_EXPIRED, subscription);
  }, [authService]);

  const handleCloseDialog = () => {
    setDialog({ ...dialog, show: false });
    dialog.action();
  };

  return (
    <Provider store={store}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <ToastContextProvider>
            <GlobalSpinnerContextProvider>
              <ApplicationLoadingPage />
              {(authService.isLoading || isRehydratingSession) && <Spinner isVisible isMask />}
              {(!authService.isLoading && !isRehydratingSession) && (
                <BrowserRouter
                  future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                  }}
                >
                  <RedirectHandler />
                  <ErrorContextProvider>
                    <ErrorBoundary>
                      <SocketSessionProvided>
                        <Header setToggleMenu={setToggleMenu} />
                        {shouldRenderLoginRoutes
                          ? (
                            <Routes>
                              <Route path={getAbsoluteRoute(ROUTES.LOGIN)} element={<Login />} />
                              <Route path="*" element={<Navigate to={getAbsoluteRoute(ROUTES.LOGIN)} replace />} />
                            </Routes>
                          )
                          : (
                            <>
                              {authService.isLogged && (
                                <Routes>
                                  {authService.canAccess() && authService.canSeeFile() && (
                                    <>
                                      <Route path={`${getAbsoluteRoute(ROUTES.FILES)}/:id`} element={<Files />} />
                                      <Route path={getAbsoluteRoute(ROUTES.FILES_LIST)} element={<FilesList />} />
                                      <Route path="*" element={<Navigate to={getAbsoluteRoute(ROUTES.FILES_LIST)} replace />} />
                                    </>
                                  )}
                                  {authService.canAccess() && !authService.canSeeFile() && (
                                    <>
                                      <Route path={getAbsoluteRoute(ROUTES.DASHBOARD)} element={<Dashboard toggleMenu={toggleMenu} />} />
                                      <Route path={getAbsoluteRoute(ROUTES.CR_OVERVIEW)} element={<CampaignRequestsOpenSearch />} />
                                      <Route path={getAbsoluteRoute(ROUTES.SUB_CR_OVERVIEW)} element={<SubCROverviewOpenSearch />} />
                                      <Route path={`${getAbsoluteRoute(ROUTES.CR_DETAIL)}/:id/:version/:tab`} element={<CampaignDetail />} />
                                      <Route path={`${getAbsoluteRoute(ROUTES.CR_DETAIL)}/:id/:tab`} element={<CampaignDetail />} />
                                      <Route path={`${getAbsoluteRoute(ROUTES.CR_DETAIL)}/:id/:version`} element={<CampaignDetail />} />
                                      <Route path={`${getAbsoluteRoute(ROUTES.CR_DETAIL)}/:id`} element={<CampaignDetail />} />
                                      <Route path={`${getAbsoluteRoute(ROUTES.SUB_CR_DETAIL)}/:id/:version/:tab`} element={<SubCampaignDetail />} />
                                      <Route path={`${getAbsoluteRoute(ROUTES.SUB_CR_DETAIL)}/:id/:tab`} element={<SubCampaignDetail />} />
                                      <Route path={`${getAbsoluteRoute(ROUTES.SUB_CR_DETAIL)}/:id`} element={<SubCampaignDetail />} />
                                      <Route path={`${getAbsoluteRoute(ROUTES.ADMINISTRATION)}/*`} element={<Administration />} />
                                      <Route path={getAbsoluteRoute(ROUTES.STAND_IN)} element={(<StandIn />)} />
                                      <Route
                                        path="/*" // if routes not matched, redirect to dashboard or files list based on user type
                                        element={(<Navigate to={getAbsoluteRoute(isFileOnlyUser(authService) ? ROUTES.FILES_LIST : ROUTES.DASHBOARD)} replace />)}
                                      />
                                    </>
                                  )}

                                  {/* Denied page for users without access */}
                                  {!authService.canAccess() && !authService.canSeeFile() && (
                                    <>
                                      <Route path={getAbsoluteRoute(ROUTES.DENIED_PAGE)} element={<DeniedPage />} />
                                      <Route path="*" element={<Navigate to={getAbsoluteRoute(ROUTES.DENIED_PAGE)} replace />} />
                                    </>
                                  )}
                                </Routes>
                              )}
                            </>
                          )}
                      </SocketSessionProvided>
                    </ErrorBoundary>
                  </ErrorContextProvider>
                </BrowserRouter>
              )}
              {/* This makes token expiration before manual logout
              {authService?.canAccess() && <IdleTimerContainer />} */}
            </GlobalSpinnerContextProvider>
          </ToastContextProvider>
          {dialog.show && (
            <ConfirmDialog
              title={t(dialog.title) as string || ''}
              close={handleCloseDialog}
              confirmBtnText={t(dialog.btnText) as string}
            >
              <p>{t(dialog.message)}</p>
            </ConfirmDialog>
          )}
        </ThemeProvider>
      </StyledEngineProvider>
    </Provider>
  );
};

export default (App);
